/**
 * Où MediaPipe calcule : sur le GPU (WebGL, défaut) ou sur le processeur (XNNPACK). Sur
 * certains GPU Android (Adreno 830 : 50 ms par image et plantages au démarrage), le
 * processeur est meilleur. Le diagnostic MESURE les deux et mémorise le plus rapide pour les
 * jeux (décision 47). PUR, sauf les deux fonctions de stockage qui prennent le `Storage`.
 */
export type Delegate = 'GPU' | 'CPU';

export const DELEGATES: readonly Delegate[] = ['GPU', 'CPU'];
export const DEFAULT_DELEGATE: Delegate = 'GPU';
export const DELEGATE_KEY = 'barbichette.delegate';

export interface DelegateMeasure {
  readonly delegate: Delegate;
  /** Images analysées par seconde (0 : aucune). */
  readonly analysisFps: number;
  /** Coût moyen d'une analyse (ms) ; `null` si inconnu. */
  readonly avgMs: number | null;
  /** Appels de MediaPipe qui ont échoué pendant la mesure. */
  readonly errors: number;
}

/** Marge en deçà de laquelle deux cadences sont jugées égales (le GPU garde alors la main). */
const TIE_RATIO = 0.9;
/** Un délégué sain l'emporte sur un délégué qui plante s'il tient au moins cette part de sa cadence. */
const HEALTHY_RATIO = 0.5;

const fastest = (pool: readonly DelegateMeasure[]): DelegateMeasure =>
  pool.reduce((a, b) => (b.analysisFps > a.analysisFps ? b : a));

/**
 * Le délégué le plus rapide ; un délégué sain l'emporte sur un délégué qui a planté s'il n'est
 * pas plus de deux fois plus lent ; à cadence proche, le GPU (il laisse le processeur au reste
 * de la page). Rien de valable → GPU.
 */
export function chooseDelegate(measures: readonly DelegateMeasure[]): Delegate {
  const usable = measures.filter((m) => m.analysisFps > 0);
  if (usable.length === 0) return DEFAULT_DELEGATE;
  const healthy = usable.filter((m) => m.errors === 0);
  const pool =
    healthy.length > 0 &&
    fastest(healthy).analysisFps >= fastest(usable).analysisFps * HEALTHY_RATIO
      ? healthy
      : usable;
  const best = fastest(pool);
  const gpu = pool.find((m) => m.delegate === 'GPU');
  if (gpu && gpu.analysisFps >= best.analysisFps * TIE_RATIO) return 'GPU';
  return best.delegate;
}

const isDelegate = (value: unknown): value is Delegate => DELEGATES.includes(value as Delegate);

export function loadDelegate(storage: Storage): Delegate {
  try {
    const value = storage.getItem(DELEGATE_KEY);
    return isDelegate(value) ? value : DEFAULT_DELEGATE;
  } catch {
    return DEFAULT_DELEGATE;
  }
}

export function saveDelegate(storage: Storage, delegate: Delegate): void {
  try {
    storage.setItem(DELEGATE_KEY, delegate);
  } catch {
    // Stockage indisponible (navigation privée, quota) : on retiendra le défaut.
  }
}
